import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type TdHTMLAttributes,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Drawer,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  theme as antdTheme,
} from 'antd';
import type { TableProps } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { useResizableColumns, space } from '@viq-admin/ui';
import { brandColors } from '@viq-admin/theme';
import type {
  BatchLead,
  BatchLeadFieldsApi,
  BatchLeadNotesApi,
  BatchLeadsFetcher,
  BatchTagsApi,
  CaptureType,
  CustomTagDefinition,
  LeadFieldsKey,
  LeadFieldsPatch,
  LeadTag,
} from './types';
import { TagsCell } from './TagsCell';
import { AssignmentCell } from './AssignmentCell';
import { CellSyncIndicator, EditableSelectCell, EditableTextCell, NameEditor } from './EditableCells';
import { NotesCell } from './NotesCell';
import { useLeadNoteSync, type LeadNoteSyncStore } from './useLeadNoteSync';
import { useLeadRowSync, type LeadFieldStatus, type LeadRowSyncStore } from './useLeadRowSync';
import { INLINE_EDIT_COPY, LEAD_FIELD_LABEL, VISUALLY_HIDDEN, rowsCarryLeadFields } from './inlineEdit';
import { RepeatScanBadge } from './RepeatScanBadge';
import { formatCapturedTime } from './relativeTime';
import './batchTable.css';
import { isIncompleteUnsent } from '../leadRules/flags';
import { failureSummary } from '../leadRules/labels';
import { LEAD_RULES_COPY } from '../leadRules/copy';
import {
  DEFAULT_LEAD_SORT,
  GRID_COPY,
  LEAD_CELL_TEXT,
  capturedDayOptions,
  countsFilterProps,
  countsSorter,
  filterLeads,
  hasActiveFilter,
  pickFilterProps,
  pickOptions,
  searchFilterProps,
  sortProps,
  tagLabel,
  tagOptions,
  temperatureOptions,
  temperatureSorter,
  textSorter,
  valuePickFilter,
  type LeadFilterPredicate,
  type LeadFilters,
  type LeadSort,
} from './leadGridSortFilter';

/** One column a CRM lens adds to the grid. */
export interface GridLensColumn {
  key: string;
  title: ReactNode;
  width: number;
  render: (lead: BatchLead) => ReactNode;
}

/**
 * A "lens" on the leads grid (port plan step 6, the unified CRM grid): the
 * CRM Clean, Match and Send tabs are this same grid — same columns, order,
 * saved widths, sort, filters, inline editing, Notes and lead drawer — with a
 * lens that only changes which rows show, adds the "In your CRM" status
 * column right after Name, may own the row selection, and brings its own
 * toolbar in place of the Active / Deleted strip.
 *
 * A lens works on the whole event: its rows are the screen's one load of
 * every ACTIVE lead at the event (`rows`, shared with the tab numbers), so a
 * table with a lens fetches nothing itself, and writes the row changes it
 * makes (tags, assignment) back through `onLeadRowPatched`. Without a lens
 * the table is the Leads tab exactly as it always was.
 */
export interface GridLens {
  /** Names the lens; the table resets its sort and filters when it changes. */
  key: string;
  /** The event's active leads, as the screen loaded them once for every lens. */
  rows: GridLensRows;
  /** Which of the loaded rows the lens shows. */
  rowFilter: (lead: BatchLead) => boolean;
  /** The "In your CRM" column, pinned right after Name. */
  statusColumn: Omit<GridLensColumn, 'key'> & {
    /** Ascending sort key; gives the column a header sort. */
    sortValue?: (lead: BatchLead) => number;
    /** The label the column's pick-list filter offers and matches. */
    filterValue?: (lead: BatchLead) => string;
  };
  /** More lens columns, after the status column (Match's CRM owner). */
  extraColumns?: GridLensColumn[];
  /** The sort the lens opens on. Omitted → the grid's own (Captured, newest first). */
  defaultSort?: LeadSort | null;
  /** Row selection the lens owns (Send's ticks, Match's bulk owner). */
  selection?: {
    selectedIds: string[];
    /** The whole new set of ticked ids (ids the lens doesn't show included). */
    onChange: (ids: string[]) => void;
    /** `null` = tickable; otherwise why not (the box is disabled, with this as its tooltip). */
    disabledReason: (lead: BatchLead) => string | null;
    /** A row box's accessible name. */
    ariaLabel?: (lead: BatchLead) => string;
  };
  /** Replaces the Active / Deleted strip. */
  toolbar?: ReactNode;
  /** The grid's empty text when no row passes `rowFilter`. */
  emptyText?: ReactNode;
  /** The Email cell on this lens (Clean). */
  emailEdit?: {
    /**
     * Replaces the inline editor's own email rule here (Clean: the CRM
     * engine's shape, so nothing is saved that the engine then rejects).
     */
    validate: (value: string) => string | null;
    /**
     * When the grid's own inline editing is off (no `leadFieldsApi`, or a
     * backend without its route): the lens's own editor for this row's
     * email, or null to show it read-only.
     */
    fallback?: (lead: BatchLead) => GridLensEmailCell | null;
  };
  /** An inline edit was just queued from this lens. */
  onLeadEdit?: (lead: BatchLead, patch: LeadFieldsPatch) => void;
  /** The lead drawer closed (alongside the table's own `onLeadDetailClose`). */
  onLeadDetailClose?: () => void;
}

/** A lens's rows (`GridLens.rows`). */
export interface GridLensRows {
  /** Every active lead at the event; `[]` until they have loaded. */
  leads: BatchLead[];
  /** The rows haven't loaded yet. */
  loading: boolean;
  /** The load failed: what to say (the grid shows it with Retry). */
  error: string | null;
  onRetry: () => void;
  /**
   * The screen's stores' epochs when these rows were read: the table lets
   * the stores forget saves these rows already carry. Null until loaded.
   */
  epochs: { field: number; note: number } | null;
}

/** A lens's own Email editor for one row (`GridLens.emailEdit.fallback`). */
export interface GridLensEmailCell {
  /** The email the row shows (a value being saved, or one that failed). */
  value: string | null | undefined;
  status: LeadFieldStatus;
  /** Read-only for now (a save is out, or one is waiting on its re-check). */
  locked?: boolean;
  onSave: (email: string) => void;
  onRetry: () => void;
  onDiscard: () => void;
}

interface Props {
  /** A CRM lens (port plan step 6) — see `GridLens`. Absent → the Leads tab. */
  lens?: GridLens;
  /**
   * Conference scope. When `null` (the portal's stakeholder single-pane), the
   * fetcher is called without an event filter — the shell's fetcher is expected
   * to omit `conferenceId` from the query in that case. The console always
   * passes a real id.
   */
  conferenceId: string | null;
  /** Shell-provided cursor-paginated leads fetcher. */
  fetchLeads: BatchLeadsFetcher;
  /** Shell-provided tag + assignment API for the editable cells. */
  tagsApi: BatchTagsApi;
  /** Renders the lead-detail body inside the drawer (each shell's own). */
  renderLeadDetail: (leadId: string) => ReactNode;
  /**
   * Read-only — TagsCell/AssignmentCell render without their editors and the
   * custom tag-definitions fetch is skipped. Used by the portal's stakeholder
   * single-pane. Default keeps the editable cells, so the console is unchanged.
   */
  readOnly?: boolean;
  /**
   * Change this to force a re-fetch of the first page. The leads list is plain
   * component state rather than react-query, so there is no key to invalidate;
   * threading a token through the load effect re-runs the fetch WITHOUT
   * remounting, which keeps scroll position. Used after a lead move, where the
   * moved rows must stop showing under the conference they left.
   */
  reloadToken?: number;
  /**
   * Change this to re-fetch the rows ALREADY LOADED: page 1, then further
   * pages (same page size) until as many rows as were loaded are back or the
   * list ends. Sort, filters and selection stay; so do unconfirmed inline
   * edits. `BatchScreen` bumps it when the lead drawer closes, where a
   * first-page reload would drop every "Load more" / "Load all" page and
   * reshuffle a sort over them. Absent or unchanged → nothing re-fetches.
   */
  refreshToken?: number;
  /**
   * When true, renders antd row-selection checkboxes — the console's
   * move-leads affordance and the portal's bulk actions. Controlled via
   * `selectedLeadIds` + `onSelectedLeadIdsChange`. Default off → no
   * checkboxes, so a shell that drives neither is unchanged.
   */
  selectable?: boolean;
  /** Controlled selection (person ids) — only used when `selectable`. */
  selectedLeadIds?: string[];
  /** Fires the new selection whenever the checkboxes change. */
  onSelectedLeadIdsChange?: (ids: string[]) => void;
  /**
   * Mirrors the Active/Deleted tab out to the shell. Default undefined →
   * nobody observes it (console unchanged). The portal's bulk-actions bar
   * needs it: a removed lead accepts Restore and nothing else.
   */
  onTabChange?: (tab: 'active' | 'deleted') => void;
  /**
   * Renders per-row actions on the Deleted tab (e.g. the portal's rep-restore
   * button). `reload` re-fetches the first page after a mutation. Default
   * undefined → no actions column (console unchanged).
   */
  renderDeletedRowActions?: (lead: BatchLead, reload: () => void) => ReactNode;
  /**
   * Returns the scrollable ancestor. When provided, the leads table's antd
   * `sticky` header + horizontal scrollbar anchor to THIS element instead of
   * `window`, so the horizontal scrollbar tracks the visible viewport rather
   * than sitting below the fold at the bottom of the (tall) table.
   */
  getScrollContainer?: () => HTMLElement | null;
  /**
   * Pixels antd's sticky header should dock BELOW the top of the scroll
   * container, so it docks under another sticky element sharing that same
   * container instead of painting over it. Default `0` — a shell that
   * doesn't pass it (or has no such sibling) is unchanged. `BatchScreen`
   * passes `CRM_STAGE_STRIP_HEIGHT` here whenever its `crm-tab-toolbar` strip
   * is also pinned to the same container (fix round 1, Critical).
   */
  offsetHeader?: number;
  /**
   * Fires when the lead-detail drawer closes — whatever the operator did in
   * there (most often: added notes until a company lead-rules requirement
   * was met) can change what THIS table and the shell around it show, and
   * this component has no idea what those are. Default undefined → no-op,
   * so a shell that doesn't pass it is unchanged.
   */
  onLeadDetailClose?: () => void;
  /**
   * Names the Assigned-to cells offer while typing (the connected CRM's owner
   * roster, spec 2026-10-02 §5.1). Absent or empty → plain inputs, unchanged.
   */
  assigneeSuggestions?: string[];
  /**
   * How many leads the Active tab lists in all — the conference's non-deleted
   * leads, which is what the KPI tiles' `kpi.total_scans` counts. Read only on
   * the Active tab, where it completes the loaded-rows cue ("Filtering the 50
   * loaded of 312 leads"). Absent (the stakeholder single-pane, or tiles still
   * loading) → the cue reads "Filtering the 50 loaded leads". Never changes
   * what loads.
   */
  activeLeadsTotal?: number;
  /**
   * Inline editing of Name, Company, Title, Email, Phone and Temp (spec
   * 2026-09-17 §2, viq-admin#56). Its PRESENCE makes those cells editable, on
   * the Active tab only (D11: deleted leads are tombstones), independently of
   * `readOnly`, which keeps governing tags and assignment (D10) — but only
   * once loaded rows carry `firstName` / `lastName`, the sign of a backend
   * that has the route (`rowsCarryLeadFields`). Absent, or an older backend →
   * the cells render exactly as they always have.
   */
  leadFieldsApi?: BatchLeadFieldsApi;
  /**
   * The sync store the edits queue on, hosted by the caller so saves in
   * flight outlive this table (`BatchScreen` unmounts it on the CRM tabs).
   * Absent → the table hosts its own.
   */
  leadRowSync?: LeadRowSyncStore;
  /**
   * The Notes column's note API (port plan step 5). Its PRESENCE makes the
   * column editable, on the Active tab only, independently of `readOnly` —
   * the `leadFieldsApi` pattern. Absent → the column shows the notes
   * read-only. (The column itself shows only when rows carry `latestNote`.)
   */
  leadNotesApi?: BatchLeadNotesApi;
  /**
   * The store the Notes column's saves go through, hosted by the caller so a
   * save in flight outlives this table (`BatchScreen` unmounts it on the CRM
   * tabs): a remounted table shows confirmed saves over the rows it loads,
   * and a save still out as saving. Absent → the table hosts its own.
   */
  leadNoteSync?: LeadNoteSyncStore;
  /**
   * A tag or assignment change this table made to a row, for a host that
   * keeps rows of its own in step (the unified CRM grid's shared rows). With
   * a lens it is the only place the change goes: the lens's rows are the
   * host's.
   */
  onLeadRowPatched?: (leadId: string, patch: Partial<BatchLead>) => void;
}

const TYPE_COLOR: Record<CaptureType, string> = {
  voice: 'purple',
  badge: 'blue',
  card: 'green',
};

// Temperature pill colors mirror the brand status pills so a manager scanning
// the table reads temperature with the same visual grammar as the tags.
const TEMPERATURE_COLOR: Record<string, string> = {
  hot: brandColors.pillExCustomer, // red
  warm: brandColors.pillCompetitor, // orange
  cold: brandColors.pillContact, // blue
};

/** The Temp pill — the read-only cell and the editable one show the same. */
function temperaturePill(v: string): ReactNode {
  const color = TEMPERATURE_COLOR[v] ?? brandColors.pillLead;
  return (
    <Tag color={color} style={{ borderRadius: 12, margin: 0 }}>
      {v}
    </Tag>
  );
}

/** First / last together are the Name cell's one sync state. */
function nameStatus(store: LeadRowSyncStore, leadId: string): LeadFieldStatus {
  const first = store.status(leadId, 'firstName');
  const last = store.status(leadId, 'lastName');
  if (first.kind === 'failed') return first;
  if (last.kind === 'failed') return last;
  return first.kind === 'saving' ? first : last;
}

/** "email", "name", "email and phone" — what a failure announcement names. */
function fieldsPhrase(fields: LeadFieldsKey[]): string {
  const words = Array.from(
    new Set(fields.map((f) => (f === 'firstName' || f === 'lastName' ? 'name' : LEAD_FIELD_LABEL[f]))),
  );
  return words.join(' and ');
}

/**
 * Advisory outline on a blank cell (gh-31 §4.6): an INSET box-shadow rather
 * than a border, so the row's height and antd's cell layout are untouched.
 * Yellow means "worth filling in before you send" — it never blocks anything;
 * the blocking rules stay in the CRM flow (`isReady`).
 *
 * `undefined` is NOT blank (fix round 2, Important 2). The three states are
 * distinct and only two of them are the cell's business:
 *
 *   - a value          → no outline, render it (a malformed-but-present
 *                        `bob@` included — the operator needs to see it)
 *   - `null` / blank   → outline: the backend looked and there is nothing
 *                        there. This is the finding the column exists for,
 *                        and it behaves exactly as it always has.
 *   - `undefined`      → NO outline: the field is absent from the response,
 *                        so nothing is known about it either way.
 *
 * That third case is reachable. Email and Phone are new on `GET
 * /v1/admin/leads` and this SPA deploys separately from the backend, so a
 * rollback leaves every row with `email`/`phone` simply missing — and the
 * old `value && …` test, which cannot tell missing from empty, painted a
 * yellow warning down both entire columns. Absence of information must never
 * present as a finding.
 */
function blankCellStyle(
  value: string | null | undefined,
  color: string,
): React.CSSProperties | undefined {
  if (value === undefined) return undefined;
  return value && value.trim() !== '' ? undefined : { boxShadow: `inset 0 0 0 1px ${color}` };
}

/** "Incomplete: Notes 12/500 · Buying time" — the Name icon's tooltip and
 *  accessible name, built from the row's `completeness`. */
function incompleteText(failures: Parameters<typeof failureSummary>[0]): string {
  return `${LEAD_RULES_COPY.incompleteTag}: ${failureSummary(failures)}`;
}

const PAGE_SIZE = 50;

/** The tab a freshly mounted table starts on — read back by the mount effect
 *  that republishes that fresh state to the shell. */
const INITIAL_TAB: 'active' | 'deleted' = 'active';

/** Width of the pinned selection checkbox column — shared between the scroll
 *  width budget and `rowSelection.columnWidth` so the two can't drift apart. */
const SELECTION_COL_WIDTH = 40;

/**
 * Leads table. Cursor-paginated ("Load more"), Active / Deleted tabs, row click
 * opens the shared Lead Detail drawer, EDITABLE tags (TagPicker) + assignment
 * via the shell-provided `tagsApi`. Ported from the portal's BatchTable.
 *
 * Additive, all default-off so a shell that passes none of them renders
 * byte-identically: `readOnly` (stakeholder single-pane), `selectable` +
 * `reloadToken` + `onTabChange` (the console's move-leads selection and the
 * portal's bulk actions, plus the re-fetch after either), and
 * `renderDeletedRowActions` (rep-restore on the Deleted tab).
 */
export function BatchTable({
  lens,
  conferenceId,
  fetchLeads,
  tagsApi,
  renderLeadDetail,
  readOnly,
  reloadToken,
  refreshToken,
  selectable,
  selectedLeadIds,
  onSelectedLeadIdsChange,
  onTabChange,
  renderDeletedRowActions,
  getScrollContainer,
  offsetHeader,
  onLeadDetailClose,
  assigneeSuggestions,
  activeLeadsTotal,
  leadFieldsApi,
  leadRowSync,
  leadNotesApi,
  leadNoteSync,
  onLeadRowPatched,
}: Props) {
  const { token } = antdTheme.useToken();
  const [tab, setTab] = useState<'active' | 'deleted'>(INITIAL_TAB);
  // The rows: this table's own pages, or a lens's (the screen's shared load
  // of the whole event, which this table never fetches itself).
  const [ownLeads, setLeads] = useState<BatchLead[]>([]);
  const lensRows = lens?.rows;
  const leads = lensRows ? lensRows.leads : ownLeads;
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [ownLoading, setLoading] = useState(false);
  const [ownError, setError] = useState<string | null>(null);
  const loading = lensRows ? lensRows.loading : ownLoading;
  const error = lensRows ? null : ownError;
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  // Inline editing. The caller's store when it hosts one (BatchScreen), else
  // this table's own; an absent API leaves it inert and nothing editable.
  const ownLeadSync = useLeadRowSync({ api: leadRowSync ? undefined : leadFieldsApi });
  const leadSync = leadRowSync ?? ownLeadSync;
  const leadSyncVersion = useSyncExternalStore(leadSync.subscribe, leadSync.getVersion);
  // Read by loadFirstPage through a ref, so the store never becomes a
  // dependency of the load (a new identity there would refetch).
  const leadSyncRef = useRef(leadSync);
  leadSyncRef.current = leadSync;
  // Deploy skew: the shell may pass the API before the backend has the
  // route (viq-backend#117), where every save would 404 as "This lead was
  // deleted". The same backend change adds first / last to every list row,
  // so editing waits for a loaded row that carries them; until then the
  // grid looks exactly as it does without the API. Once seen it stays for
  // this table's life (the Notes column's `latestNote` guard), so an empty
  // list doesn't make the editors blink out and back.
  const [leadFieldsSeen, setLeadFieldsSeen] = useState(false);
  const leadFieldsBackend = leadFieldsSeen || rowsCarryLeadFields(leads);
  if (leadFieldsBackend && !leadFieldsSeen) setLeadFieldsSeen(true);
  const inlineEditOn = !!leadFieldsApi && leadFieldsBackend;
  const editable = inlineEditOn && tab === 'active';
  // Notes: editable by API presence on the Active tab (the column itself
  // shows only once rows carry `latestNote`, below).
  const notesEditable = !!leadNotesApi && tab === 'active';

  // Header sort and filters, CONTROLLED (antd reports them through onChange)
  // so this table can reset them, see which rows they leave on screen, and say
  // when they only cover the loaded pages. Starts on Captured, newest first —
  // the order this grid has always opened in.
  // A lens may open on its own sort (Match: decisions first).
  const lensMode = !!lens;
  const defaultSort: LeadSort | null =
    lens && lens.defaultSort !== undefined ? lens.defaultSort : DEFAULT_LEAD_SORT;
  const [sort, setSort] = useState<LeadSort | null>(defaultSort);
  const [filters, setFilters] = useState<LeadFilters>({});

  // Sort and filters belong to ONE list of leads. The table isn't keyed, so
  // without this they would carry over to the next conference, to the Deleted
  // tab, and across the console's company-scope switch (which rebuilds the
  // shell's fetcher, so `fetchLeads` changing identity is that signal) — a
  // filter applied to a list the operator has never looked at. Adjusted
  // DURING render, not from an effect, so no frame paints the new list under
  // the old filters.
  // A CRM lens switch (Clean → Match → Send) is a new list too.
  const lensKey = lens?.key;
  const [listScope, setListScope] = useState({ conferenceId, tab, fetchLeads, lensKey });
  if (
    listScope.conferenceId !== conferenceId ||
    listScope.tab !== tab ||
    listScope.fetchLeads !== fetchLeads ||
    listScope.lensKey !== lensKey
  ) {
    setListScope({ conferenceId, tab, fetchLeads, lensKey });
    setSort(defaultSort);
    setFilters({});
  }

  // Bumped by every first-page load and on unmount, so a "Load all" still
  // paging through a list that has since been replaced stops instead of
  // appending its rows to the new one.
  const listGenerationRef = useRef(0);
  useEffect(
    () => () => {
      listGenerationRef.current += 1;
    },
    [],
  );
  const [loadingAll, setLoadingAll] = useState(false);

  // BatchScreen renders this table CONDITIONALLY — the CRM stage tabs
  // unmounts it for the whole time the operator is on Clean/Match/Send — and a
  // remount starts over on the Active tab with nothing ticked. The shell keeps
  // its own mirror of both (the bulk bar's action set and its selected ids),
  // and the Tabs handler below is the only thing that ever writes them, so
  // without this a round trip out of Leads and back leaves the bar offering
  // Restore over active rows and holding ids from the Deleted tab. Republish
  // the fresh state on mount; the handler keeps it current from then on.
  useEffect(() => {
    onTabChange?.(INITIAL_TAB);
    onSelectedLeadIdsChange?.([]);
    // Mount only: after this the operator's own tab switches and ticks own it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Custom tag definitions for the active scope. Loaded once per BatchTable
  // mount; the picker repopulates its CUSTOM dropdown on each open. Skipped in
  // read-only mode (the picker is hidden anyway).
  const tagDefsQuery = useQuery<CustomTagDefinition[]>({
    queryKey: ['batchCustomTagDefinitions'],
    queryFn: async () => (await tagsApi.fetchTagDefinitions()).definitions,
    staleTime: 60_000,
    enabled: !readOnly,
  });
  const tagDefs = tagDefsQuery.data ?? [];

  // Notes column saves: the caller's store when it hosts one (BatchScreen),
  // else this table's own. A list load reads its epoch when it starts; a
  // save confirmed after that may be missing from what it returns, so it
  // keeps showing over the row until a later load carries it — the Notes
  // column's counterpart to the sync store's epoch / prune above.
  const ownNoteSync = useLeadNoteSync();
  const noteSync = leadNoteSync ?? ownNoteSync;
  const noteSyncVersion = useSyncExternalStore(noteSync.subscribe, noteSync.getVersion);
  const noteSyncRef = useRef(noteSync);
  noteSyncRef.current = noteSync;
  // Read by the refresh through a ref, so the row count is never a
  // dependency of the load (it would refetch on every page).
  const loadedCountRef = useRef(0);
  loadedCountRef.current = leads.length;

  const loadFirstPage = useCallback(async () => {
    listGenerationRef.current += 1;
    // Saves confirmed before this load starts are in what it returns; ones
    // confirmed while it is out are not, and keep showing over it.
    const syncEpoch = leadSyncRef.current.epoch();
    const noteEpoch = noteSyncRef.current.epoch();
    setLoading(true);
    setError(null);
    try {
      const result = await fetchLeads({
        conferenceId: conferenceId as string,
        limit: PAGE_SIZE,
        deleted: tab === 'deleted',
      });
      setLeads(result.leads);
      noteSyncRef.current.prune(noteEpoch);
      setNextCursor(result.nextCursor);
      leadSyncRef.current.prune(syncEpoch);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Couldn’t load the leads.');
    } finally {
      setLoading(false);
    }
  }, [conferenceId, tab, fetchLeads]);

  /**
   * Re-fetches the rows already loaded (`refreshToken`): page 1, then the
   * next pages in the same page size until at least as many rows are back as
   * were loaded, or the list ends. The rows are swapped in once, at the end,
   * so the grid never drops to one page in between. Sort and filters are
   * state of this table and stay as they are; unconfirmed inline edits keep
   * showing over the fresh rows (the same epoch / prune as a first-page
   * load). Stops quietly if the list is replaced mid-way.
   */
  const refreshLoadedRows = useCallback(async () => {
    listGenerationRef.current += 1;
    const generation = listGenerationRef.current;
    const wanted = loadedCountRef.current;
    const syncEpoch = leadSyncRef.current.epoch();
    const noteEpoch = noteSyncRef.current.epoch();
    const stale = () => listGenerationRef.current !== generation;
    const base = { conferenceId: conferenceId as string, limit: PAGE_SIZE, deleted: tab === 'deleted' };
    setLoading(true);
    setError(null);
    try {
      const first = await fetchLeads(base);
      if (stale()) return;
      let rows = first.leads;
      let next = first.nextCursor;
      let cursor = next;
      while (cursor && rows.length < wanted) {
        const page = await fetchLeads({ ...base, cursor });
        if (stale()) return;
        rows = [...rows, ...page.leads];
        next = page.nextCursor;
        // A server handing back the cursor it was given would loop forever.
        cursor = page.nextCursor === cursor ? null : page.nextCursor;
      }
      setLeads(rows);
      noteSyncRef.current.prune(noteEpoch);
      setNextCursor(next);
      leadSyncRef.current.prune(syncEpoch);
    } catch (err) {
      if (!stale()) setError((err as { message?: string }).message ?? 'Couldn’t load the leads.');
    } finally {
      // A load that replaced the list owns `loading` from then on.
      if (!stale()) setLoading(false);
    }
  }, [conferenceId, tab, fetchLeads]);

  // Before the first-page effect, so that if both ever fire in one commit the
  // first-page load (which starts a new generation) is the one that lands.
  // The token is compared, not just listed: a new list scope changes this
  // callback's identity, and that alone must not refresh anything.
  // A lens's rows are the screen's; it re-reads them itself.
  const seenRefreshTokenRef = useRef(refreshToken);
  useEffect(() => {
    if (seenRefreshTokenRef.current === refreshToken) return;
    seenRefreshTokenRef.current = refreshToken;
    if (!lensMode) void refreshLoadedRows();
  }, [refreshLoadedRows, refreshToken, lensMode]);

  // reloadToken is a dependency on purpose: it is a signal, not a value the
  // effect reads. Bumping it from BatchScreen after a move re-runs the fetch.
  useEffect(() => {
    if (!lensMode) void loadFirstPage();
  }, [loadFirstPage, reloadToken, lensMode]);

  // A lens's rows landed: the stores may forget the saves they carry (read
  // after the save, or written through by the screen). Once per load.
  const lensEpochs = lensRows?.epochs;
  useEffect(() => {
    if (!lensEpochs) return;
    leadSyncRef.current.prune(lensEpochs.field);
    noteSyncRef.current.prune(lensEpochs.note);
  }, [lensEpochs]);

  async function loadMore() {
    if (!nextCursor) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchLeads({
        conferenceId: conferenceId as string,
        cursor: nextCursor,
        limit: PAGE_SIZE,
        deleted: tab === 'deleted',
      });
      setLeads((prev) => [...prev, ...result.leads]);
      setNextCursor(result.nextCursor);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Couldn’t load more leads.');
    } finally {
      setLoading(false);
    }
  }

  /**
   * Loads every remaining page, one after another, until the server says
   * there are none — the loaded-rows cue's "Load all". Sorting and filtering
   * stay in the browser (port plan D7); this only makes the loaded rows the
   * whole list. Stops quietly if the list is replaced mid-way (a conference,
   * tab or scope switch, or a reload).
   */
  async function loadAll() {
    if (!nextCursor || loading) return;
    const generation = listGenerationRef.current;
    let cursor: string | null = nextCursor;
    setLoading(true);
    setLoadingAll(true);
    setError(null);
    try {
      while (cursor) {
        const result = await fetchLeads({
          conferenceId: conferenceId as string,
          cursor,
          limit: PAGE_SIZE,
          deleted: tab === 'deleted',
        });
        if (listGenerationRef.current !== generation) return;
        setLeads((prev) => [...prev, ...result.leads]);
        setNextCursor(result.nextCursor);
        // A server handing back the cursor it was given would loop forever.
        cursor = result.nextCursor === cursor ? null : result.nextCursor;
      }
    } catch (err) {
      if (listGenerationRef.current === generation) {
        setError((err as { message?: string }).message ?? 'Couldn’t load more leads.');
      }
    } finally {
      setLoadingAll(false);
      // A first-page load that replaced the list owns `loading` from then on.
      if (listGenerationRef.current === generation) setLoading(false);
    }
  }

  // Tag and assignment changes go to the host's rows too (with a lens, only
  // there). Through a ref, so the callbacks below stay stable.
  const rowPatchRef = useRef({ lensMode, onLeadRowPatched });
  rowPatchRef.current = { lensMode, onLeadRowPatched };
  const patchRow = useCallback((id: string, patch: Partial<BatchLead>) => {
    const { lensMode: viaLens, onLeadRowPatched: host } = rowPatchRef.current;
    if (!viaLens) setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    host?.(id, patch);
  }, []);

  /** Replaces the tags on a single lead (called by TagsCell after the picker's
   *  optimistic mutation completes). */
  const updateLeadTags = useCallback((id: string, tags: LeadTag[]) => patchRow(id, { tags }), [patchRow]);

  /** Replaces assignedTo on a single lead after AssignmentCell saves. */
  const updateLeadAssignment = useCallback(
    (id: string, assignedTo: string | null) => patchRow(id, { assignedTo }),
    [patchRow],
  );

  // The Notes column shows only once loaded rows carry `latestNote` (null
  // included): an older backend omits the key, and the grid must then look
  // exactly as it did, not offer "Add a note" on every row. Once seen it
  // stays for this table's life, so an empty list (a quiet Deleted tab)
  // doesn't make the column blink out and back.
  const [notesSeen, setNotesSeen] = useState(false);
  const showNotesColumn = notesSeen || leads.some((l) => l.latestNote !== undefined);
  if (showNotesColumn && !notesSeen) setNotesSeen(true);

  /**
   * The rows as the grid shows, sorts and filters them: each loaded row with
   * its confirmed Notes saves that the load predates laid over it (the saved
   * note as `latestNote`, a create counted in `notesCount`; nothing else on
   * the row is touched, so a tag, an assignment or an inline edit made
   * meanwhile is never undone), then its unconfirmed, failed and
   * just-confirmed inline edits — those only while editing is on. With
   * nothing to lay over, the loaded rows themselves (the same array).
   */
  const shownLeads = useMemo(
    () => {
      const withNotes = noteSync.overlay(leads);
      return editable ? withNotes.map((l) => leadSync.overlay(l)) : withNotes;
    },
    // The versions are the stores' change signals: overlay() reads their state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editable, leads, leadSync, leadSyncVersion, noteSync, noteSyncVersion],
  );

  /** Queue an inline edit, tagged with the event it was made under. */
  const saveFields = (id: string, patch: LeadFieldsPatch) => {
    leadSync.enqueue(id, patch, { conferenceId });
    if (lens?.onLeadEdit) {
      const row = shownLeads.find((l) => l.id === id);
      if (row) lens.onLeadEdit(row, patch);
    }
  };

  // The rows the grid itself works on: a lens's subset of the loaded rows, or
  // all of them. Pick lists, filters and the selection guard all read these.
  const gridRows = useMemo(
    () => (lens ? shownLeads.filter(lens.rowFilter) : shownLeads),
    [lens, shownLeads],
  );

  // What the grid's live region says: a failure while it stands, or the
  // rate-limit wait while it lasts.
  const notice = editable ? leadSync.lastNotice() : null;
  const rateLimited = editable && leadSync.rateLimited();
  let noticeText = '';
  if (notice?.kind === 'rate_limited' && rateLimited) {
    noticeText = INLINE_EDIT_COPY.rateLimited;
  } else if (notice?.kind === 'failed' && leadSync.status(notice.leadId, notice.fields[0]).kind === 'failed') {
    const name = leads.find((l) => l.id === notice.leadId)?.preview || 'this lead';
    noticeText = INLINE_EDIT_COPY.notSaved(fieldsPhrase(notice.fields), name, notice.message);
  }

  const totalLoadedLabel = useMemo(
    () => `${leads.length} loaded${nextCursor ? ', more available' : ' (end of results)'}`,
    [leads.length, nextCursor],
  );

  const filtering = hasActiveFilter(filters);
  // The default order (Captured, newest first) and no sort at all both show
  // the loaded rows in the order they arrived, so neither claims anything the
  // next page could contradict. Any other sort does.
  const sorting =
    sort !== null &&
    !(defaultSort && sort.columnKey === defaultSort.columnKey && sort.order === defaultSort.order);

  // Pick-list options come from the rows as shown; recomputed only when they
  // change.
  const options = useMemo(
    () => ({
      companyName: pickOptions(gridRows, LEAD_CELL_TEXT.companyName),
      title: pickOptions(gridRows, LEAD_CELL_TEXT.title),
      temperature: temperatureOptions(gridRows),
      assignedTo: pickOptions(gridRows, LEAD_CELL_TEXT.assignedTo),
      repName: pickOptions(gridRows, LEAD_CELL_TEXT.repName),
      createdAt: capturedDayOptions(gridRows),
      captureType: pickOptions(gridRows, LEAD_CELL_TEXT.captureType),
      tags: tagOptions(gridRows),
    }),
    [gridRows],
  );

  // A lens's status column filters on the lens's own labels.
  const statusFilterValue = lens?.statusColumn.filterValue;
  const statusFilter = statusFilterValue
    ? valuePickFilter('crmStatus', gridRows, statusFilterValue, filters)
    : null;
  const extraPredicates: Record<string, LeadFilterPredicate> = statusFilter
    ? { crmStatus: statusFilter.predicate }
    : {};

  // A filter must never leave a hidden row ticked. Selection uses
  // preserveSelectedRowKeys (it has to survive "Load more"), so a row ticked
  // and then filtered out — or edited until it no longer matches — would stay
  // selected out of sight, and bulk archive / tag / assign and Move would act
  // on it. While any filter is on, keep the selection to the rows on screen.
  // With no filter nothing is hidden, so the selection is left exactly as it
  // always was.
  const visibleLeadIds = useMemo(
    () =>
      filtering ? new Set(filterLeads(gridRows, filters, extraPredicates).map((l) => l.id)) : null,
    // extraPredicates follows the lens, which `gridRows` already tracks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtering, gridRows, filters],
  );
  useEffect(() => {
    if (!selectable || !visibleLeadIds || !selectedLeadIds?.length) return;
    const kept = selectedLeadIds.filter((id) => visibleLeadIds.has(id));
    if (kept.length !== selectedLeadIds.length) onSelectedLeadIdsChange?.(kept);
  }, [selectable, visibleLeadIds, selectedLeadIds, onSelectedLeadIdsChange]);
  // The same guard for a lens's own selection: Send must never send a ticked
  // row a filter hides.
  const lensSelection = lens?.selection;
  const lensSelectionRef = useRef(lensSelection);
  lensSelectionRef.current = lensSelection;
  const lensSelectedIds = lensSelection?.selectedIds;
  useEffect(() => {
    const sel = lensSelectionRef.current;
    if (!sel || !visibleLeadIds || !lensSelectedIds?.length) return;
    const kept = lensSelectedIds.filter((id) => visibleLeadIds.has(id));
    if (kept.length !== lensSelectedIds.length) sel.onChange(kept);
  }, [visibleLeadIds, lensSelectedIds]);

  // D7: sorts and filters only see the loaded pages. Say so while more remain.
  // The total is the Active tab's (the KPI tiles' non-deleted count) and is
  // only shown when it is plausible; otherwise the cue reads without it.
  const cueTotal =
    tab === 'active' && typeof activeLeadsTotal === 'number' && activeLeadsTotal > leads.length
      ? activeLeadsTotal
      : null;
  const showLoadedCue = (filtering || sorting) && !!nextCursor;

  const handleTableChange: TableProps<BatchLead>['onChange'] = (_pagination, nextFilters, sorter) => {
    setFilters(nextFilters);
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    setSort(
      s?.order && s.columnKey !== undefined
        ? { columnKey: String(s.columnKey), order: s.order }
        : null,
    );
  };

  /** Read-only Company / Title / Email / Phone text: the value, or a dash. */
  const plainText = (v: string | null | undefined) =>
    v?.trim() ? v : <Typography.Text type="secondary">—</Typography.Text>;

  /**
   * A Company / Title / Email / Phone cell: the inline editor while editing
   * is on, otherwise the read-only text exactly as these columns always
   * rendered it.
   */
  const textCell =
    (field: 'company' | 'title' | 'email' | 'phone') => (v: string | null | undefined, row: BatchLead) => {
      const emailEdit = field === 'email' ? lens?.emailEdit : undefined;
      if (editable) {
        return (
          <EditableTextCell
            field={field}
            value={v}
            leadName={row.preview || '(no name)'}
            status={leadSync.status(row.id, field)}
            validate={emailEdit?.validate}
            onSave={(value) => saveFields(row.id, { [field]: value })}
            onRetry={() => leadSync.retry(row.id, [field])}
            onDiscard={() => leadSync.discard(row.id, [field])}
          />
        );
      }
      // Inline editing off: a lens may still edit the email its own way
      // (Clean, through the CRM API's lead edit, as Clean always has).
      const own = emailEdit?.fallback?.(row);
      if (!own) return plainText(v);
      if (own.locked) {
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: space.xs, minWidth: 0 }}>
            {plainText(own.value)}
            <CellSyncIndicator
              status={own.status}
              what={`email for ${row.preview || '(no name)'}`}
              descriptionId={`viq-lens-email-${row.id}`}
              onRetry={own.onRetry}
              onDiscard={own.onDiscard}
            />
          </span>
        );
      }
      return (
        <EditableTextCell
          field="email"
          value={own.value}
          leadName={row.preview || '(no name)'}
          status={own.status}
          validate={emailEdit?.validate}
          onSave={own.onSave}
          onRetry={own.onRetry}
          onDiscard={own.onDiscard}
        />
      );
    };

  const baseColumns: NonNullable<TableProps<BatchLead>['columns']> = [
    {
      // Every column carries an explicit `key` equal to its `dataIndex`: antd
      // reports a sort as `columnKey: column.key`, and the saved-widths map
      // (useResizableColumns, `key ?? dataIndex`) keeps reading the same names.
      key: 'preview',
      dataIndex: 'preview',
      title: 'Name',
      ...sortProps('preview', textSorter(LEAD_CELL_TEXT.preview), sort),
      ...searchFilterProps('preview', filters, token.colorPrimary),
      // Pinned so the row stays identifiable while the wide right-hand
      // columns scroll under it (PR #10, folded in). antd requires a width
      // on fixed columns; resizing still works — useResizableColumns
      // overrides `width` and spreads the rest of the column through.
      fixed: 'left' as const,
      width: 200,
      ellipsis: true,
      // Company lead rules (spec 2026-09-14 §3.4): an incomplete, unsent lead
      // is marked on the pinned Name cell by a red "!" before the name (see
      // render). The cell keeps `data-incomplete` as the hook; the red inset
      // edge it used to carry is gone — it sat right beside the icon and said
      // the same thing. No new column, so the pinned column order and the
      // resizable-columns storage key are unchanged.
      onCell: (row: BatchLead) =>
        isIncompleteUnsent(row)
          ? ({ 'data-incomplete': 'true' } as TdHTMLAttributes<HTMLTableCellElement>)
          : {},
      render: (v: string, row: BatchLead) => (
        // Truncation lives on the link, not the cell: antd's `ellipsis` clips
        // at the <td>, and the badge follows the name, so on a long name it is
        // the badge that gets cut — the only signal that a lead is a repeat
        // scan. Shrinking the name instead keeps the ↻ visible.
        <span style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {/* A small red icon BEFORE the name, not an "Incomplete" pill after
              it, so the name keeps the room; the icons line up down the
              column. Hover (or a screen reader) says what's missing. */}
          {isIncompleteUnsent(row) && row.completeness && (
            <Tooltip title={incompleteText(row.completeness.failures)}>
              <ExclamationCircleFilled
                aria-label={incompleteText(row.completeness.failures)}
                style={{ color: token.colorError, marginRight: space.xs, flexShrink: 0 }}
              />
            </Tooltip>
          )}
          <Typography.Link
            onClick={() => setSelectedLeadId(row.id)}
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {v || '(no name)'}
          </Typography.Link>
          <span style={{ flexShrink: 0, display: 'inline-flex' }}>
            <RepeatScanBadge
              match={row.repeatScanMatch ?? null}
              history={row.repeatScanHistory ?? []}
            />
          </span>
          {/* The link keeps opening the drawer; the pencil (row hover or
              focus) edits First / Last in a popover. */}
          {editable && (
            <NameEditor
              row={row}
              leadName={v || '(no name)'}
              status={nameStatus(leadSync, row.id)}
              onSave={(patch) => saveFields(row.id, patch)}
              onRetry={() => leadSync.retry(row.id, ['firstName', 'lastName'])}
              onDiscard={() => leadSync.discard(row.id, ['firstName', 'lastName'])}
            />
          )}
        </span>
      ),
    },
    ...(lens
      ? [
          {
            // Pinned beside Name: what the CRM tab is about stays in view.
            key: 'crmStatus',
            title: lens.statusColumn.title,
            fixed: 'left' as const,
            width: lens.statusColumn.width,
            ...(lens.statusColumn.sortValue
              ? sortProps(
                  'crmStatus',
                  (a: BatchLead, b: BatchLead) =>
                    lens.statusColumn.sortValue!(a) - lens.statusColumn.sortValue!(b),
                  sort,
                )
              : {}),
            ...(statusFilter?.props ?? {}),
            render: (_: unknown, row: BatchLead) => lens.statusColumn.render(row),
          },
          ...(lens.extraColumns ?? []).map((c) => ({
            key: c.key,
            title: c.title,
            width: c.width,
            render: (_: unknown, row: BatchLead) => c.render(row),
          })),
        ]
      : []),
    {
      key: 'companyName',
      dataIndex: 'companyName',
      title: 'Company',
      ...sortProps('companyName', textSorter(LEAD_CELL_TEXT.companyName), sort),
      ...pickFilterProps('companyName', options.companyName, filters),
      width: 140,
      ellipsis: true,
      onCell: (row: BatchLead) => ({ style: blankCellStyle(row.companyName, token.colorWarning) }),
      render: textCell('company'),
    },
    {
      key: 'title',
      dataIndex: 'title',
      title: 'Title',
      ...sortProps('title', textSorter(LEAD_CELL_TEXT.title), sort),
      ...pickFilterProps('title', options.title, filters),
      width: 130,
      ellipsis: true,
      render: textCell('title'),
    },
    {
      // `null` is the ONLY "missing" signal a backend that projects this
      // field sends (it coerces blank/whitespace to null at the mapping
      // layer) — blankCellStyle's `.trim()` check is belt-and-suspenders for
      // the same reason it already is on Company/Assigned-to, not a second
      // thing being tested for. A malformed-but-present value (e.g. `bob@`)
      // is truthy and non-blank, so it renders verbatim with no outline —
      // the operator needs to see it. A backend that does NOT project it
      // (rolled back past this field) sends `undefined`, which
      // `blankCellStyle` treats as "nothing is known", not as a warning.
      key: 'email',
      dataIndex: 'email',
      title: 'Email',
      ...sortProps('email', textSorter(LEAD_CELL_TEXT.email), sort),
      ...searchFilterProps('email', filters, token.colorPrimary),
      width: 180,
      ellipsis: true,
      onCell: (row: BatchLead) => ({ style: blankCellStyle(row.email, token.colorWarning) }),
      render: textCell('email'),
    },
    {
      key: 'phone',
      dataIndex: 'phone',
      title: 'Phone',
      ...sortProps('phone', textSorter(LEAD_CELL_TEXT.phone), sort),
      ...searchFilterProps('phone', filters, token.colorPrimary),
      width: 120,
      ellipsis: true,
      onCell: (row: BatchLead) => ({ style: blankCellStyle(row.phone, token.colorWarning) }),
      render: textCell('phone'),
    },
    {
      key: 'temperature',
      dataIndex: 'temperature',
      title: 'Temp',
      ...sortProps('temperature', temperatureSorter, sort),
      ...pickFilterProps('temperature', options.temperature, filters),
      // Room for the sort and filter icons beside the title (was 60).
      width: 80,
      render: (v: string | null, row: BatchLead) => {
        if (editable) {
          return (
            <EditableSelectCell
              value={v}
              leadName={row.preview || '(no name)'}
              status={leadSync.status(row.id, 'temperature')}
              renderValue={temperaturePill}
              onSave={(temperature) => saveFields(row.id, { temperature })}
              onRetry={() => leadSync.retry(row.id, ['temperature'])}
              onDiscard={() => leadSync.discard(row.id, ['temperature'])}
            />
          );
        }
        if (!v) return <Typography.Text type="secondary">—</Typography.Text>;
        return temperaturePill(v);
      },
    },
    {
      key: 'assignedTo',
      dataIndex: 'assignedTo',
      title: 'Assigned to',
      ...sortProps('assignedTo', textSorter(LEAD_CELL_TEXT.assignedTo), sort),
      ...pickFilterProps('assignedTo', options.assignedTo, filters),
      width: 140,
      onCell: (row: BatchLead) => ({ style: blankCellStyle(row.assignedTo, token.colorWarning) }),
      render: (_: string | null | undefined, row: BatchLead) => (
        <AssignmentCell
          personId={row.id}
          assignedTo={row.assignedTo ?? null}
          readOnly={readOnly}
          tagsApi={tagsApi}
          suggestions={assigneeSuggestions}
          onAssignedChange={(next) => updateLeadAssignment(row.id, next)}
        />
      ),
    },
    {
      key: 'counts',
      title: 'Notes / Tasks',
      ...sortProps('counts', countsSorter, sort),
      ...countsFilterProps(filters),
      width: 100,
      align: 'center' as const,
      render: (_: unknown, row: BatchLead) => (
        <Typography.Text>
          {row.notesCount ?? 0} / {row.openTaskCount ?? 0}
        </Typography.Text>
      ),
    },
    {
      key: 'repName',
      dataIndex: 'repName',
      title: 'Scanned by',
      ...sortProps('repName', textSorter(LEAD_CELL_TEXT.repName), sort),
      ...pickFilterProps('repName', options.repName, filters),
      width: 110,
      ellipsis: true,
      render: (v: string | undefined, row: BatchLead) => v ?? row.repOid.slice(0, 8) + '…',
    },
    {
      key: 'createdAt',
      dataIndex: 'createdAt',
      title: 'Captured',
      width: 110,
      render: (v: string) => formatCapturedTime(v),
      // The default sort (newest first) now lives in `DEFAULT_LEAD_SORT`, the
      // initial value of the controlled `sort` state, not `defaultSortOrder`.
      ...sortProps('createdAt', (a: BatchLead, b: BatchLead) => a.createdAt.localeCompare(b.createdAt), sort),
      // By the viewer's LOCAL day — the day the cell's time is shown in.
      ...pickFilterProps('createdAt', options.createdAt, filters),
    },
    {
      key: 'captureType',
      dataIndex: 'captureType',
      title: 'Type',
      ...sortProps('captureType', textSorter(LEAD_CELL_TEXT.captureType), sort),
      ...pickFilterProps('captureType', options.captureType, filters),
      width: 90,
      render: (v: CaptureType) => <Tag color={TYPE_COLOR[v]}>{v}</Tag>,
    },
    {
      key: 'tags',
      dataIndex: 'tags',
      title: 'Tags',
      // Sorted by the labels the filter lists ("VIP", "Status: customer", …).
      ...sortProps('tags', textSorter((r) => LEAD_CELL_TEXT.tags(r).map(tagLabel)), sort),
      ...pickFilterProps('tags', options.tags, filters),
      width: 180,
      render: (_: LeadTag[], row: BatchLead) => (
        <TagsCell
          personId={row.id}
          tags={row.tags ?? []}
          readOnly={readOnly}
          customTagDefinitions={tagDefs}
          tagsApi={tagsApi}
          onTagsChange={(tags) => updateLeadTags(row.id, tags)}
        />
      ),
    },
    ...(showNotesColumn
      ? [
          {
            // The rep's own words, immediately before the AI paraphrase
            // (port plan D6: Summary stays, unchanged). Two lines at most,
            // the full note on hover; editable by API presence.
            key: 'notes',
            title: 'Notes',
            ...sortProps('notes', textSorter(LEAD_CELL_TEXT.notes), sort),
            ...searchFilterProps('notes', filters, token.colorPrimary),
            width: 320,
            render: (_: unknown, row: BatchLead) => (
              <NotesCell
                lead={row}
                leadName={row.preview || '(no name)'}
                api={notesEditable ? leadNotesApi : undefined}
                noteSync={noteSync}
                conferenceId={conferenceId}
                onOpenLead={() => setSelectedLeadId(row.id)}
              />
            ),
          },
        ]
      : []),
    {
      key: 'aiSummary',
      dataIndex: 'aiSummary',
      title: 'Summary',
      ...sortProps('aiSummary', textSorter(LEAD_CELL_TEXT.aiSummary), sort),
      width: 260,
      ellipsis: true,
      render: (v: string | null) =>
        v ? (
          <Typography.Text>{v.replace(/\r?\n/g, ' ')}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    ...(tab === 'deleted'
      ? [
          {
            key: 'deletedAt',
            dataIndex: 'deletedAt',
            title: 'Deleted at',
            ...sortProps('deletedAt', textSorter(LEAD_CELL_TEXT.deletedAt), sort),
            width: 150,
            render: (v: string | null) => (v ? new Date(v).toLocaleString() : '—'),
          },
          ...(renderDeletedRowActions
            ? [
                {
                  key: 'restore',
                  title: '',
                  // Pinned right for the same reason Name is pinned left —
                  // the action stays reachable without scrolling back.
                  fixed: 'right' as const,
                  width: 60,
                  render: (_: unknown, row: BatchLead) =>
                    renderDeletedRowActions(row, () => void loadFirstPage()),
                },
              ]
            : []),
        ]
      : []),
  ];

  // The declared scroll width IS the column budget: antd needs it to enable
  // the fixed-column layout, and a number smaller than the columns (the old
  // hard-coded 1400 under ~1690px of columns) made the table both scroll AND
  // squeeze. Sum the defaults (dragged widths override per column at render),
  // plus the pinned selection column when it's rendered at all — true in
  // both modes, or the budget undercounts by SELECTION_COL_WIDTH whenever
  // `selectable`.
  const scrollX =
    baseColumns.reduce((sum, col) => sum + (typeof col.width === 'number' ? col.width : 0), 0) +
    (selectable || lensSelection ? SELECTION_COL_WIDTH : 0);

  // Key bumped from 'batchLeadsV2' (Task 6, Email/Phone columns): the two
  // new columns land between Title and Temp, shifting every column to their
  // right. A stored v2 width map has no entries for them and would keep
  // applying its OLD widths to those shifted columns, hiding the new ones off
  // to the right of whatever fit under the old, narrower layout.
  const { columns, components } = useResizableColumns('batchLeadsV3', baseColumns);

  return (
    <div style={{ margin: '0 16px 16px' }}>
      {error && <Alert showIcon type="error" message={error} style={{ marginBottom: space.md }} />}
      {lensRows?.error && (
        // The screen's load of the event's leads failed: said plainly, with
        // the way back (a lens can't show anyone without it).
        <Alert
          showIcon
          type="error"
          data-testid="batch-lens-rows-error"
          message={lensRows.error}
          action={
            <Button size="small" onClick={lensRows.onRetry}>
              {GRID_COPY.retry}
            </Button>
          }
          style={{ marginBottom: space.md }}
        />
      )}

      {/* Table flush with the card's sides: the card body drops its side
          padding and the Tabs row and Load-more row put it back on
          themselves (space.md = the small card's own 12px). Inset, the sticky
          header stopped 12px short of the card's edges, so once stuck it sat
          between two card-coloured strips. Flush, it meets the page gutters,
          which share its colour (colorBgLayout === Table.headerBg in both
          modes), and reads as one band across the pane. */}
      <Card size="small" styles={{ body: { paddingInline: 0 } }}>
        {inlineEditOn && (
          // Inline-edit failures and the rate-limit wait, announced politely
          // (the cells show them too; a tooltip alone would not be heard).
          <div role="status" aria-live="polite" data-testid="inline-edit-live" style={VISUALLY_HIDDEN}>
            {noticeText}
          </div>
        )}
        {lens ? (
          // A lens has no Deleted tab: its own toolbar takes the strip's place,
          // with how many of the event's leads it shows.
          <div
            data-testid="batch-lens-toolbar"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: space.md,
              flexWrap: 'wrap',
              marginBottom: space.md,
              paddingInline: space.md,
            }}
          >
            <div style={{ flex: '1 1 320px', minWidth: 0 }}>{lens.toolbar}</div>
            <Space size={space.md}>
              {rateLimited && (
                <Typography.Text type="warning" data-testid="inline-edit-rate-limited">
                  {INLINE_EDIT_COPY.rateLimited}
                </Typography.Text>
              )}
              <Typography.Text type="secondary" data-testid="batch-lens-count">
                {loading ? GRID_COPY.loading : GRID_COPY.lensCount(gridRows.length, leads.length)}
              </Typography.Text>
            </Space>
          </div>
        ) : (
        <Tabs
          activeKey={tab}
          onChange={(key) => {
            // Selection uses preserveSelectedRowKeys, so it survives a tab
            // switch. Carrying it into Deleted lets an operator try to move a
            // soft-deleted lead, which the backend rejects with "Leads not
            // found" for rows plainly visible on screen — and would offer the
            // portal's bulk tag/assign on rows that are gone. Clear on every
            // switch.
            onSelectedLeadIdsChange?.([]);
            setTab(key as 'active' | 'deleted');
            onTabChange?.(key as 'active' | 'deleted');
          }}
          size="small"
          style={{ marginBottom: space.md, paddingInline: space.md }}
          items={[
            { key: 'active', label: 'Active' },
            // "Deleted", not "Deleted by rep": admin deletes land in this same
            // tab, and there is only one removed state (ADR 017). The `key`
            // stays 'deleted' — it is the wire filter value, not display copy.
            { key: 'deleted', label: 'Deleted' },
          ]}
          tabBarExtraContent={
            rateLimited ? (
              // Visible while a save waits out a 429, so a slow save explains
              // itself (the grid's live region announces it).
              <Space size={space.md}>
                <Typography.Text type="warning" data-testid="inline-edit-rate-limited">
                  {INLINE_EDIT_COPY.rateLimited}
                </Typography.Text>
                <Typography.Text type="secondary">{totalLoadedLabel}</Typography.Text>
              </Space>
            ) : (
              <Typography.Text type="secondary">{totalLoadedLabel}</Typography.Text>
            )
          }
        />
        )}
        {showLoadedCue && (
          // D7: the sort / filter above covers only the loaded pages. Only
          // rendered while one is active and more pages remain, so the card's
          // usual tabs → table → Load-more layout is untouched otherwise.
          <Alert
            type="warning"
            showIcon
            data-testid="batch-loaded-rows-cue"
            style={{ marginInline: space.md, marginBottom: space.md, paddingBlock: space.xs }}
            message={
              <span>
                {GRID_COPY.loadedCue(leads.length, cueTotal)}
                {' · '}
                <Button
                  type="link"
                  size="small"
                  onClick={() => void loadAll()}
                  loading={loadingAll}
                  disabled={loading && !loadingAll}
                  style={{ padding: 0, height: 'auto' }}
                >
                  {GRID_COPY.loadAll}
                </Button>
              </span>
            }
          />
        )}
        <Table
          rowKey="id"
          // The ONE hook for this table's styling: TABLE_STICKY_BG_CSS
          // (@viq-admin/theme — sticky-layer background) and batchTable.css
          // (square sticky-header corners) both key on it. antd puts
          // `className` on `.ant-table-wrapper`, the ancestor of both
          // `.ant-table-sticky-holder` and `.ant-table-sticky-scroll`. Keep it
          // single: a second `className` prop silently replaces this one.
          className="viq-batch-leads-table"
          dataSource={gridRows}
          // A lens whose rows failed must not read as an empty success.
          locale={
            lensRows?.error
              ? { emptyText: lensRows.error }
              : lens?.emptyText !== undefined
                ? { emptyText: lens.emptyText }
                : undefined
          }
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: scrollX }}
          // Sticky header + horizontal scrollbar, anchored to the shell's scroll
          // container (not `window`, which never scrolls on this screen) so the
          // horizontal bar is always reachable. Refs populate during commit —
          // before any scroll — so the `document.body` fallback is only a
          // type-safe momentary default for antd's initial setup pass.
          //
          // `offsetHeader` (fix round 1, Critical): this same container also
          // holds `BatchScreen`'s pinned `crm-tab-toolbar`, sticky at `top: 0`
          // — the exact row antd's own sticky header wants. Without an
          // offset the header (z-index `zIndexTableFixed + 1` in antd
          // 5.29.3, one above the strip's old z-index) painted over the
          // strip on scroll. `offsetHeader` docks the header BELOW the strip
          // instead, so the two never contest the same pixels.
          sticky={
            getScrollContainer
              ? {
                  getContainer: () => getScrollContainer() ?? document.body,
                  offsetHeader: offsetHeader ?? 0,
                }
              : true
          }
          columns={columns}
          components={components}
          onChange={handleTableChange}
          // Selection checkboxes only when the shell asks for them (the
          // console's move-leads affordance, the portal's bulk actions).
          // preserveSelectedRowKeys keeps the selection across "Load more".
          rowSelection={
            lensSelection
              ? {
                  // The lens owns these ticks (Send's commit set, Match's bulk
                  // owner); kept across a reload like the Leads selection.
                  selectedRowKeys: lensSelection.selectedIds,
                  onChange: (keys) => lensSelectionRef.current?.onChange(keys as string[]),
                  preserveSelectedRowKeys: true,
                  getCheckboxProps: (row: BatchLead) =>
                    ({
                      disabled: lensSelection.disabledReason(row) !== null,
                      'aria-label': lensSelection.ariaLabel?.(row),
                    }) as React.ComponentProps<typeof Checkbox>,
                  renderCell: (_checked, row, _index, node) => {
                    const reason = lensSelection.disabledReason(row);
                    return reason ? (
                      <Tooltip title={reason}>
                        <span>{node}</span>
                      </Tooltip>
                    ) : (
                      node
                    );
                  },
                  fixed: true,
                  columnWidth: SELECTION_COL_WIDTH,
                }
              : selectable
              ? {
                  selectedRowKeys: selectedLeadIds ?? [],
                  onChange: (keys) => onSelectedLeadIdsChange?.(keys as string[]),
                  preserveSelectedRowKeys: true,
                  // The checkbox column sits left of the pinned Name column,
                  // so it has to be pinned too — otherwise the left-fixed
                  // block is non-contiguous and the checkboxes scroll away
                  // from the rows they select (PR #10).
                  fixed: true,
                  columnWidth: SELECTION_COL_WIDTH,
                }
              : undefined
          }
        />
        {/* A lens has already loaded the whole event. */}
        {!lens && (
          <div style={{ marginTop: space.md, paddingInline: space.md, textAlign: 'right' }}>
            <Space>
              <Button onClick={loadMore} disabled={!nextCursor || loading}>
                Load more
              </Button>
            </Space>
          </div>
        )}
      </Card>
      <Drawer
        title="Lead detail"
        width={820}
        open={!!selectedLeadId}
        onClose={() => {
          setSelectedLeadId(null);
          onLeadDetailClose?.();
          lens?.onLeadDetailClose?.();
        }}
        destroyOnHidden
      >
        {selectedLeadId && renderLeadDetail(selectedLeadId)}
      </Drawer>
    </div>
  );
}
